import { useQuery } from "@tanstack/react-query";
import { Badge, Card, Input, Page, Select, Table, type TableColumn } from "@theg1group/ui";
import { useState } from "react";
import { Link } from "react-router-dom";
import { AddressLink } from "../components/AddressLink.tsx";
import { ChainBadge } from "../components/ChainBadge.tsx";
import { RiskBadge } from "../components/RiskBadge.tsx";
import { api } from "../lib/api.ts";
import { fmtAgo, fmtDate, fmtNum } from "../lib/format.ts";
import type { AddressRow, ChainInfo, Rows } from "../lib/types.ts";

export function Addresses() {
	const [q, setQ] = useState("");
	const [chain, setChain] = useState("");

	const chains = useQuery({
		queryKey: ["chains"],
		queryFn: () => api.get<ChainInfo[]>("/api/wallets/chains"),
	});
	const list = useQuery({
		queryKey: ["addresses", q, chain],
		queryFn: () =>
			api.get<Rows<AddressRow>>(
				`/api/addresses?q=${encodeURIComponent(q)}&chain=${encodeURIComponent(chain)}`,
			),
	});

	const columns: TableColumn<AddressRow>[] = [
		{ id: "chain", header: "Chain", accessor: (r) => <ChainBadge chain={r.chain} /> },
		{
			id: "address",
			header: "Address",
			accessor: (r) => (
				<AddressLink id={r.id} address={r.address} display={r.display} head={10} tail={6} />
			),
		},
		{ id: "kind", header: "Kind", accessor: (r) => r.kind ?? "—" },
		{
			id: "risk",
			header: "Risk",
			accessor: (r) => (
				<span className="inline-flex items-center gap-1">
					<RiskBadge score={r.riskScore} />
					{r.isSupernode && <Badge variant="info">supernode</Badge>}
				</span>
			),
		},
		{
			id: "labels",
			header: "Labels",
			accessor: (r) => (
				<div className="flex flex-wrap gap-1">
					{r.labels.map((l) => (
						<Badge key={l} variant="warning" size="sm">
							{l}
						</Badge>
					))}
					{!r.labels.length && <span className="text-muted">—</span>}
				</div>
			),
		},
		{
			id: "partners",
			header: "Partners",
			accessor: (r) =>
				r.partners.length ? r.partners.join(", ") : <span className="text-muted">—</span>,
		},
		{ id: "tx", header: "Tx", accessor: (r) => fmtNum(r.txCount) },
		{
			id: "seen",
			header: "Last seen",
			accessor: (r) => <span title={fmtDate(r.lastSeen)}>{fmtAgo(r.lastSeen)}</span>,
		},
	];

	return (
		<Page
			title="Addresses"
			description="Every address seen in partner wallets, traces and label feeds."
			actions={
				<Link to="/graph" className="text-primary text-sm hover:underline">
					Open graph →
				</Link>
			}
		>
			<div className="mb-3 flex flex-wrap gap-2">
				<div className="w-96">
					<Input
						value={q}
						onChange={(e) => setQ(e.target.value)}
						placeholder="Search address / partner / label…"
						className="font-mono"
					/>
				</div>
				<div className="w-48">
					<Select
						value={chain}
						onChange={(e) => setChain(e.target.value)}
						options={[
							{ value: "", label: "All chains" },
							...(chains.data ?? []).map((c) => ({ value: c.id, label: c.name })),
						]}
					/>
				</div>
			</div>
			<Card>
				<div className="overflow-x-auto">
					<Table
						columns={columns}
						data={list.data?.rows ?? []}
						keyExtractor={(r) => r.id}
						loading={list.isLoading}
						emptyMessage="No addresses match."
					/>
				</div>
			</Card>
		</Page>
	);
}
